import api from "./api";

/** Item da listagem GET /despesas-extratos-avaliacoes/(legacy|solicitacoes) */
export type DespesaExtratoComprovanteItem = {
  extratoId?: number | null;
  sindicatoId?: number | null;
  cnpj?: string;
  sigla?: string;
  nome?: string;
  solicitacao?: string;
  situacao?: { id?: number; nome?: string } | string | null;
  /** true = ação bloqueada até aceite do extrato (visão sindicato). */
  bloqueado?: boolean | null;
  comprovanteEnviadoEm?: string | null;
  solicitadoCorrecaoEm?: string | null;
  comprovanteCorrigidoEm?: string | null;
  formularioEnviadoEm?: string | null;
};

export type DespesasExtratosAvaliacoesListResponse = {
  anoReferenciaSelecionado: number;
  avaliacoesModelo?: { anoReferencia: number; ativo?: boolean }[];
  comprovantes: DespesaExtratoComprovanteItem[];
};

/** Detalhe V2: GET /despesas-extratos-avaliacoes/extratos/{id} (endpoint PAI). */
export type DespesaExtratoAvaliacaoDetalhe = {
  /** Id usado nas rotas de salvar/enviar/analise/correção. */
  comprovanteDespesasId?: number;
  /** Algumas respostas podem vir no singular. */
  comprovanteDespesaId?: number;
  extratoSolicitacaoAvaliacaoId?: number;
  extratoSolicitacaoId?: number;
  valorAprovadoSolicitacao?: number;
  /** Checkbox "Liberar valor total da solicitação" (GET). */
  liberarValorTotal?: boolean | null;
  /** Alguns payloads usam o mesmo nome do PATCH. */
  liberarValorTotalSolicitacao?: boolean | null;
  /**
   * Observação do analista na raiz (justificativa liberar valor total).
   * GET: /extratos/{id} — persiste para o usuário.
   */
  observacaoAnalista?: string | null;
  /** @deprecated Preferir observacaoAnalista (raiz). */
  observacaoLiberarValorTotalSolicitacao?: string | null;
  status?: { id?: number; nome?: string } | null;
  dadosSindicato?: {
    cnpj?: string;
    nome?: string;
  };
  dadosBancarios?: {
    tipo?: string;
    tipoConta?: string;
    tipoContaDescricao?: string;
    banco?: string;
    agencia?: string;
    conta?: string;
  };
  tiposDespesas?: { id?: number; titulo?: string }[];
  despesas?: {
    id?: number;
    descricao?: string;
    tipoDespesa?: { id?: number; titulo?: string };
    valor?: number;
    nomeArquivo?: string;
    arquivoId?: number;
    emConformidade?: boolean | null;
    analise?: {
      id?: number;
      observacaoSindicato?: string;
      observacaoAnalista?: string;
      emConformidade?: boolean | null;
      solicitadoEm?: string;
      adequadoEm?: string | null;
    }[];
  }[];
};

/** Payload PATCH salvar (visão sindicato). */
export type SalvarDespesasExtratoAvaliacaoSindicatoPayload = {
  despesas: {
    /** despesas[].id do GET PAI. */
    id: number;
    descricao?: string | null;
    valor?: number | null;
    /** null quando ainda não há análise/correção no GET PAI. */
    adequacao?: {
      /** despesas[].analise[].id do GET PAI. */
      id: number;
      observacaoSindicato?: string | null;
    } | null;
  }[];
};

/** Payload PATCH salvar análise (visão analista). */
export type SalvarDespesasExtratoAvaliacaoAnalisePayload = {
  /** Checkbox "Liberar valor total da solicitação". */
  liberarValorTotalSolicitacao: boolean;
  /**
   * Observação do analista na raiz (justificativa liberar valor total).
   * PATCH: /{id}/analise
   */
  observacaoAnalista?: string | null;
  /** @deprecated Preferir observacaoAnalista (raiz). */
  observacaoLiberarValorTotalSolicitacao?: string | null;
  despesas: {
    /** despesas[].id do GET PAI. */
    despesaExtratoAvaliacaoId: number;
    /** despesas[].analise[].id; null no 1º save. */
    analiseDespesaExtratoAvaliacaoId?: number | null;
    emConformidade?: boolean | null;
    observacaoAnalista?: string | null;
  }[];
};

/** Payload POST enviar comprovantes (visão sindicato) — multipart IFormFile. */
export type EnviarDespesasExtratoAvaliacaoPayload = {
  /** Arquivo PDF do resumo (campo form `pdfResumo` → IFormFile). */
  pdfResumo: File | Blob;
  fileName?: string;
};

/** @deprecated Use DespesaExtratoComprovanteItem — mantido para imports antigos. */
export type ComprovanteDespesaSolicitacaoItem =
  DespesaExtratoComprovanteItem & {
    solicitacaoId?: number | null;
    idSolicitacaoAvaliacao?: number | null;
    idSindicato?: number | null;
    situacaoComprovante?: { id?: number; nome?: string } | string | null;
    situacaoEnvioComprovante?: string | null;
    dataEnvioComprovante?: string | null;
    enviadoEm?: string | null;
    dataSolicitacaoCorrecaoComprovante?: string | null;
    solicitacaoCorrecaoEm?: string | null;
    dataCorrecaoComprovante?: string | null;
    correcaoEm?: string | null;
    dataEnvioFormulario?: string | null;
    envioFormularioEm?: string | null;
    podeEnviarComprovantes?: boolean | null;
    envioComprovantesHabilitado?: boolean | null;
    aceiteExtrato?: boolean | null;
    extratoAceito?: boolean | null;
  };

/** @deprecated Prefer DespesasExtratosAvaliacoesListResponse */
export type ComprovantesDespesasSolicitacoesResponse =
  DespesasExtratosAvaliacoesListResponse & {
    solicitacoes?: DespesaExtratoComprovanteItem[];
  };

const parseObjectResponse = <T>(payload: unknown): T | null => {
  if (payload && typeof payload === "object") {
    return payload as T;
  }

  if (typeof payload === "string") {
    try {
      return JSON.parse(payload) as T;
    } catch (error) {
      console.error(
        "❌ Erro ao fazer parse da resposta de comprovantes:",
        error,
      );
      return null;
    }
  }

  return null;
};

const normalizeListResponse = (
  raw: DespesasExtratosAvaliacoesListResponse | null,
): DespesasExtratosAvaliacoesListResponse | null => {
  if (!raw) return null;
  const comprovantes =
    raw.comprovantes ??
    (raw as ComprovantesDespesasSolicitacoesResponse).solicitacoes ??
    [];
  return {
    ...raw,
    comprovantes: Array.isArray(comprovantes) ? comprovantes : [],
  };
};

// V1 (2025): listagem legado — Analista e Sindicato
// Endpoint: GET /api/despesas-extratos-avaliacoes/legacy
export const getDespesasExtratosAvaliacoesLegacy =
  async (): Promise<DespesasExtratosAvaliacoesListResponse | null> => {
    try {
      const response = await api.get(
        "/api/despesas-extratos-avaliacoes/legacy",
      );
      return normalizeListResponse(
        parseObjectResponse<DespesasExtratosAvaliacoesListResponse>(
          response?.data,
        ),
      );
    } catch (error) {
      console.error("❌ Erro ao buscar comprovantes (legacy):", error);
      throw error;
    }
  };

// V2 (2026+): listagem por ano — Analista e Sindicato
// Endpoint: GET /api/despesas-extratos-avaliacoes/solicitacoes?anoReferencia=ANO
export const getDespesasExtratosAvaliacoesSolicitacoes = async (
  anoReferencia: number,
): Promise<DespesasExtratosAvaliacoesListResponse | null> => {
  try {
    const response = await api.get(
      "/api/despesas-extratos-avaliacoes/solicitacoes",
      { params: { anoReferencia } },
    );
    return normalizeListResponse(
      parseObjectResponse<DespesasExtratosAvaliacoesListResponse>(
        response?.data,
      ),
    );
  } catch (error) {
    console.error(
      "❌ Erro ao buscar solicitações de comprovantes por ano:",
      error,
    );
    throw error;
  }
};

/** Alias — mantém callers antigos apontando para o endpoint V2 correto. */
export const getComprovantesDespesasSolicitacoesByAno =
  getDespesasExtratosAvaliacoesSolicitacoes;

// V2 (2026+): detalhe / expandir comprovante
// Endpoint: GET /api/despesas-extratos-avaliacoes/extratos/{extratoSolicitacaoAvaliacaoId}
export const getDespesasExtratosAvaliacaoDetalhe = async (
  extratoSolicitacaoAvaliacaoId: number | string,
): Promise<DespesaExtratoAvaliacaoDetalhe | null> => {
  try {
    const response = await api.get(
      `/api/despesas-extratos-avaliacoes/extratos/${extratoSolicitacaoAvaliacaoId}`,
    );
    return parseObjectResponse<DespesaExtratoAvaliacaoDetalhe>(response?.data);
  } catch (error) {
    console.error(
      "❌ Erro ao buscar detalhe do comprovante por extratoId:",
      error,
    );
    throw error;
  }
};

/** Payload do POST de arquivo de despesa (V2). */
export type UploadArquivoDespesaPayload = {
  tipoDespesaId: number;
  /** Somente na substituição de arquivo de despesa já existente. */
  despesaId?: number | null;
  /** Obrigatório quando tipoDespesaId = 13 (Outros). */
  descricao?: string | null;
  file: File;
};

export type UploadArquivoDespesaResponse = {
  id?: number;
  despesaId?: number;
  arquivoId?: number;
  tipoDespesaId?: number;
  nomeArquivo?: string;
  [key: string]: unknown;
};

const pickArquivoIdFromUpload = (
  raw: UploadArquivoDespesaResponse,
): number | null => {
  const nested = (raw.arquivo ?? raw.Arquivo) as
    | { id?: number; Id?: number }
    | undefined;
  const candidate =
    raw.arquivoId ??
    raw.ArquivoId ??
    raw.idArquivo ??
    raw.IdArquivo ??
    nested?.id ??
    nested?.Id ??
    null;

  if (candidate == null || candidate === "") return null;
  const n = Number(candidate);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeUploadArquivoDespesaResponse = (
  raw: UploadArquivoDespesaResponse | null,
): UploadArquivoDespesaResponse | null => {
  if (!raw || typeof raw !== "object") return raw;

  const despesaId = raw.despesaId ?? raw.DespesaId ?? raw.id ?? raw.Id ?? null;
  const arquivoId = pickArquivoIdFromUpload(raw);
  const tipoDespesaId = raw.tipoDespesaId ?? raw.TipoDespesaId ?? null;
  const nomeArquivo =
    (raw.nomeArquivo as string | undefined) ??
    (raw.NomeArquivo as string | undefined) ??
    undefined;

  return {
    ...raw,
    ...(despesaId != null
      ? { id: Number(despesaId), despesaId: Number(despesaId) }
      : {}),
    ...(arquivoId != null ? { arquivoId } : {}),
    ...(tipoDespesaId != null ? { tipoDespesaId: Number(tipoDespesaId) } : {}),
    ...(nomeArquivo != null ? { nomeArquivo } : {}),
  };
};

/** Tipo de despesa "Outros" — Descricao obrigatória no upload. */
export const TIPO_DESPESA_OUTROS_ID = 13;

// V2 (2026+): anexa/substitui arquivo de despesa (despesa só existe com arquivo)
// Endpoint: POST /api/despesas-extratos-avaliacoes/comprovantes-despesas/{comprovanteDespesasId}/arquivo-despesa
export const uploadArquivoDespesa = async (
  comprovanteDespesasId: number | string,
  payload: UploadArquivoDespesaPayload,
): Promise<UploadArquivoDespesaResponse | null> => {
  const { tipoDespesaId, despesaId, descricao, file } = payload || {};

  if (comprovanteDespesasId == null || comprovanteDespesasId === "") {
    throw new Error("comprovanteDespesasId é obrigatório.");
  }
  if (tipoDespesaId == null) {
    throw new Error("tipoDespesaId é obrigatório.");
  }
  if (!file) {
    throw new Error("File é obrigatório.");
  }

  const formData = new FormData();
  formData.append("tipoDespesaId", String(tipoDespesaId));
  formData.append("File", file, file.name);

  if (despesaId != null && Number(despesaId) > 0) {
    formData.append("DespesaId", String(despesaId));
  }

  if (descricao != null && String(descricao).trim() !== "") {
    formData.append("Descricao", String(descricao).trim());
  }

  try {
    const response = await api.post(
      `/api/despesas-extratos-avaliacoes/comprovantes-despesas/${comprovanteDespesasId}/arquivo-despesa`,
      formData,
    );
    return normalizeUploadArquivoDespesaResponse(
      parseObjectResponse<UploadArquivoDespesaResponse>(response?.data),
    );
  } catch (error) {
    console.error("❌ Erro ao enviar arquivo de despesa:", error);
    throw error;
  }
};

// V2 (2026+): remove despesa persistida
// Endpoint: DELETE /api/despesas-extratos-avaliacoes/comprovantes-despesas/{comprovanteDespesasId}/despesas/{despesaId}
export const deleteDespesaComprovante = async (
  comprovanteDespesasId: number | string,
  despesaId: number | string,
): Promise<void> => {
  if (comprovanteDespesasId == null || comprovanteDespesasId === "") {
    throw new Error("comprovanteDespesasId é obrigatório.");
  }
  if (despesaId == null || despesaId === "") {
    throw new Error("despesaId é obrigatório.");
  }

  try {
    await api.delete(
      `/api/despesas-extratos-avaliacoes/comprovantes-despesas/${comprovanteDespesasId}/despesas/${despesaId}`,
    );
  } catch (error) {
    console.error("❌ Erro ao excluir despesa do comprovante:", error);
    throw error;
  }
};

// V2 (2026+): salvar comprovantes — visão sindicato
// Endpoint: PATCH /api/despesas-extratos-avaliacoes/{comprovanteDespesasExtratoAvaliacaoId}
// Route id = comprovanteDespesasId do GET PAI (NÃO usa /extratos/).
export const salvarDespesasExtratoAvaliacaoSindicato = async (
  comprovanteDespesasExtratoAvaliacaoId: number | string,
  payload: SalvarDespesasExtratoAvaliacaoSindicatoPayload,
): Promise<void> => {
  if (
    comprovanteDespesasExtratoAvaliacaoId == null ||
    comprovanteDespesasExtratoAvaliacaoId === ""
  ) {
    throw new Error("comprovanteDespesasExtratoAvaliacaoId é obrigatório.");
  }

  try {
    await api.patch(
      `/api/despesas-extratos-avaliacoes/${comprovanteDespesasExtratoAvaliacaoId}`,
      {
        despesas: Array.isArray(payload?.despesas) ? payload.despesas : [],
      },
    );
  } catch (error) {
    console.error(
      "❌ Erro ao salvar comprovantes de despesas (sindicato V2):",
      error,
    );
    throw error;
  }
};

// V2 (2026+): salvar análise — visão analista
// Endpoint: PATCH /api/despesas-extratos-avaliacoes/{comprovanteDespesasExtratoAvaliacaoId}/analise
// Route id = comprovanteDespesasId do GET PAI (NÃO usa /extratos/).
export const salvarDespesasExtratoAvaliacaoAnalise = async (
  comprovanteDespesasExtratoAvaliacaoId: number | string,
  payload: SalvarDespesasExtratoAvaliacaoAnalisePayload,
): Promise<DespesaExtratoAvaliacaoDetalhe | null> => {
  if (
    comprovanteDespesasExtratoAvaliacaoId == null ||
    comprovanteDespesasExtratoAvaliacaoId === ""
  ) {
    throw new Error("comprovanteDespesasExtratoAvaliacaoId é obrigatório.");
  }

  try {
    const liberar = !!payload?.liberarValorTotalSolicitacao;
    const obsRaiz = liberar
      ? String(
          payload?.observacaoAnalista ??
            payload?.observacaoLiberarValorTotalSolicitacao ??
            "",
        ).trim()
      : null;

    const response = await api.patch(
      `/api/despesas-extratos-avaliacoes/${comprovanteDespesasExtratoAvaliacaoId}/analise`,
      {
        liberarValorTotalSolicitacao: liberar,
        observacaoAnalista: obsRaiz,
        despesas: Array.isArray(payload?.despesas) ? payload.despesas : [],
      },
    );
    return parseObjectResponse<DespesaExtratoAvaliacaoDetalhe>(response?.data);
  } catch (error) {
    console.error(
      "❌ Erro ao salvar análise de comprovantes (analista V2):",
      error,
    );
    throw error;
  }
};

// V2 (2026+): solicitar correção — visão analista
// Endpoint: POST /api/despesas-extratos-avaliacoes/{comprovanteDespesasExtratoAvaliacaoId}/solicitar-correcao
// Route id = comprovanteDespesasId do GET PAI (NÃO usa /extratos/).
export const solicitarCorrecaoDespesasExtratoAvaliacao = async (
  comprovanteDespesasExtratoAvaliacaoId: number | string,
): Promise<void> => {
  if (
    comprovanteDespesasExtratoAvaliacaoId == null ||
    comprovanteDespesasExtratoAvaliacaoId === ""
  ) {
    throw new Error("comprovanteDespesasExtratoAvaliacaoId é obrigatório.");
  }

  try {
    await api.post(
      `/api/despesas-extratos-avaliacoes/${comprovanteDespesasExtratoAvaliacaoId}/solicitar-correcao`,
    );
  } catch (error) {
    console.error("❌ Erro ao solicitar correção de comprovantes (V2):", error);
    throw error;
  }
};

/** Payload POST solicitar-assinatura (V2) — multipart IFormFile. */
export type SolicitarAssinaturaDespesasExtratoAvaliacaoPayload = {
  arquivo: File | Blob;
  emailAprovador: string;
  fileName?: string;
};

// V2 (2026+): solicitar assinatura — visão analista
// Endpoint: POST /api/despesas-extratos-avaliacoes/{comprovanteDespesasExtratoAvaliacaoId}/solicitar-assinatura
// Route id = comprovanteDespesasId do GET PAI (NÃO usa /extratos/).
// Body multipart: Arquivo (binary) + EmailAprovador. Sucesso: 204.
export const solicitarAssinaturaDespesasExtratoAvaliacao = async (
  comprovanteDespesasExtratoAvaliacaoId: number | string,
  payload: SolicitarAssinaturaDespesasExtratoAvaliacaoPayload,
): Promise<void> => {
  if (
    comprovanteDespesasExtratoAvaliacaoId == null ||
    comprovanteDespesasExtratoAvaliacaoId === ""
  ) {
    throw new Error("comprovanteDespesasExtratoAvaliacaoId é obrigatório.");
  }

  const email = String(payload?.emailAprovador ?? "").trim();
  if (!email) {
    throw new Error("EmailAprovador é obrigatório.");
  }

  const arquivo = payload?.arquivo;
  if (!arquivo) {
    throw new Error("Arquivo é obrigatório.");
  }

  const fileName =
    payload.fileName ||
    (arquivo instanceof File && arquivo.name
      ? arquivo.name
      : `formulario_assinatura_${new Date().toISOString().slice(0, 10)}.pdf`);

  const file =
    arquivo instanceof File
      ? arquivo
      : new File([arquivo], fileName, { type: "application/pdf" });

  const formData = new FormData();
  formData.append("Arquivo", file, file.name || fileName);
  formData.append("EmailAprovador", email);

  try {
    await api.post(
      `/api/despesas-extratos-avaliacoes/${comprovanteDespesasExtratoAvaliacaoId}/solicitar-assinatura`,
      formData,
    );
  } catch (error) {
    console.error(
      "❌ Erro ao solicitar assinatura de comprovantes (V2):",
      error,
    );
    throw error;
  }
};

// V2 (2026+): enviar comprovantes — visão sindicato
// Endpoint: POST /api/despesas-extratos-avaliacoes/{comprovanteDespesasExtratoAvaliacaoId}/enviar
// Route id = comprovanteDespesasId do GET PAI.
// Backend exige IFormFile (multipart). Mesmo padrão de uploadArquivoDespesa.
export const enviarDespesasExtratoAvaliacao = async (
  comprovanteDespesasExtratoAvaliacaoId: number | string,
  payload: EnviarDespesasExtratoAvaliacaoPayload,
): Promise<void> => {
  if (
    comprovanteDespesasExtratoAvaliacaoId == null ||
    comprovanteDespesasExtratoAvaliacaoId === ""
  ) {
    throw new Error("comprovanteDespesasExtratoAvaliacaoId é obrigatório.");
  }

  const pdfResumo = payload?.pdfResumo;
  if (!pdfResumo) {
    throw new Error("pdfResumo é obrigatório.");
  }

  const fileName =
    payload.fileName ||
    (pdfResumo instanceof File && pdfResumo.name
      ? pdfResumo.name
      : `comprovantes_despesas_${new Date().toISOString().slice(0, 10)}.pdf`);

  const file =
    pdfResumo instanceof File
      ? pdfResumo
      : new File([pdfResumo], fileName, { type: "application/pdf" });

  const formData = new FormData();
  // Campo camelCase (erro do back citou $.pdfResumo → IFormFile).
  formData.append("pdfResumo", file, file.name || fileName);

  try {
    // Igual ao upload de arquivo: sem Content-Type manual e sem transformRequest,
    // para o Axios/browser definirem multipart com boundary.
    await api.post(
      `/api/despesas-extratos-avaliacoes/${comprovanteDespesasExtratoAvaliacaoId}/enviar`,
      formData,
    );
  } catch (error) {
    console.error("❌ Erro ao enviar comprovantes de despesas (V2):", error);
    throw error;
  }
};

// Busca o preview antigo para envio de comprovantes de despesas
// Endpoint: GET /api/financeiro/getViewComprovantesDespesasByIdSindicato/{idSindicato}
export const getViewComprovantesDespesasByIdSindicato = async (idSindicato) => {
  try {
    const response = await api.get(
      `/api/financeiro/getViewComprovantesDespesasByIdSindicato/${idSindicato}`,
    );
    return response?.data || {};
  } catch (e: unknown) {
    const err = e as { response?: { status?: number } };
    const status = err?.response?.status;
    if (status === 500) {
    }
    console.error("Erro ao gerar o preview do financeiro (antigo):", e);
    throw e;
  }
};

// Busca o preview para análise de comprovantes de despesas
// Endpoint: GET /api/financeiro/getViewComprovantesDespesasAnalise/{idSindicato}
export const getViewComprovantesDespesasAnaliseByIdSindicato = async (
  idSindicato,
) => {
  try {
    const response = await api.get(
      `/api/financeiro/getViewComprovantesDespesasAnalise/${idSindicato}`,
    );
    return response?.data || {};
  } catch (e: unknown) {
    const err = e as { response?: { status?: number } };
    const status = err?.response?.status;
    if (status === 500) {
    }
    console.error("Erro ao gerar o preview do financeiro (ANÁLISE):", e);
    throw e;
  }
};

// Salva os comprovantes de despesas com payload multipart/form-data
// Endpoint: POST /api/financeiro/salvarComprovantesDespesasByIdSindicato
export const salvarComprovantesDespesasByIdSindicato = async (payload) => {
  const {
    idSindicato,
    Enviar,
    ParcelasExtrato,
    ComprovantesDespesas,
    AdequacaoCampos,
    Arquivos = [],
  } = payload || {};

  const base64ToBlob = (b64) => {
    try {
      const parts = b64.split(",");
      const meta = parts[0];
      const data = parts[1] || "";
      const mime =
        /data:(.*?);base64/.exec(meta)?.[1] || "application/octet-stream";
      const binStr = atob(data);
      const len = binStr.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    } catch {
      return null;
    }
  };

  try {
    const formData = new FormData();

    formData.append("IdSindicato", String(idSindicato ?? ""));
    formData.append("Enviar", Enviar ? "true" : "false");

    formData.append(
      "ParcelasExtrato",
      typeof ParcelasExtrato === "string"
        ? ParcelasExtrato
        : JSON.stringify(ParcelasExtrato ?? []),
    );

    formData.append(
      "ComprovantesDespesas",
      typeof ComprovantesDespesas === "string"
        ? ComprovantesDespesas
        : JSON.stringify(ComprovantesDespesas ?? []),
    );

    formData.append(
      "AdequacaoCampos",
      typeof AdequacaoCampos === "string"
        ? AdequacaoCampos
        : JSON.stringify(AdequacaoCampos ?? []),
    );

    const arquivosArr = Array.isArray(Arquivos) ? Arquivos : [Arquivos];
    arquivosArr.filter(Boolean).forEach((item) => {
      if (item?.file) {
        let fileObj = item.file;

        if (!(fileObj instanceof File || fileObj instanceof Blob)) {
          if (typeof fileObj?.base64 === "string" && fileObj?.name) {
            const blob = base64ToBlob(fileObj.base64);
            if (blob) {
              formData.append("Arquivos", blob, fileObj.name);
              return;
            }
          }
          return;
        }

        const nomeOriginal =
          fileObj instanceof File ? fileObj.name : "arquivo.pdf";

        const nome = item.identificador
          ? `${item.identificador}_${nomeOriginal}`
          : nomeOriginal;

        formData.append("Arquivos", fileObj, nome);
      } else if (item instanceof File || item instanceof Blob) {
        formData.append(
          "Arquivos",
          item,
          item instanceof File ? item.name || "arquivo.pdf" : "arquivo.pdf",
        );
      }
    });
    const resp = await api.post(
      "/api/financeiro/salvarComprovantesDespesasByIdSindicato",
      formData,
    );

    return resp.data;
  } catch (e: unknown) {
    const err = e as { response?: { data?: unknown }; message?: string };
    if (err?.response?.data) {
      console.error("Erro Backend", JSON.stringify(err.response.data, null, 2));
    } else {
      console.error("Erro:", err?.message || e);
    }
    throw e;
  }
};

/** Evento do histórico V2 de comprovantes de despesa. */
export type DespesaExtratoEventoItem = {
  id?: number;
  nome?: string;
  criadoEm?: string;
  arquivoId?: number | null;
};

// V2 (2026+): histórico/eventos do comprovante por extrato
// Endpoint: GET /api/despesas-extratos-avaliacoes/extratos/{extratoSolicitacaoAvaliacaoId}/eventos
export const getDespesasExtratosAvaliacaoEventos = async (
  extratoSolicitacaoAvaliacaoId: number | string,
): Promise<DespesaExtratoEventoItem[]> => {
  if (
    extratoSolicitacaoAvaliacaoId == null ||
    extratoSolicitacaoAvaliacaoId === ""
  ) {
    return [];
  }

  try {
    const response = await api.get(
      `/api/despesas-extratos-avaliacoes/extratos/${extratoSolicitacaoAvaliacaoId}/eventos`,
    );
    const payload = response?.data;
    if (Array.isArray(payload)) return payload as DespesaExtratoEventoItem[];
    if (payload && typeof payload === "object") {
      const eventos =
        (payload as { eventos?: DespesaExtratoEventoItem[] }).eventos ??
        (payload as { data?: DespesaExtratoEventoItem[] }).data;
      if (Array.isArray(eventos)) return eventos;
    }
    return [];
  } catch (error) {
    console.error(
      "❌ Erro ao buscar eventos do comprovante de despesa (V2):",
      error,
    );
    return [];
  }
};

// Busca o histórico de ações dos comprovantes de despesa
// Endpoint: GET /api/financeiro/getHistoricoAcaoComprovanteDespesa/{idSindicato}
export const getHistoricoAcaoComprovanteDespesa = async (idSindicato) => {
  if (!idSindicato) return [];
  try {
    const resp = await api.get(
      `/api/financeiro/getHistoricoAcaoComprovanteDespesa/${idSindicato}`,
    );

    const data =
      resp?.data?.historicoAcaoComprovanteDespesa ?? resp?.data ?? [];

    if (Array.isArray(data)) return data;

    console.warn(
      "Formato inesperado em getHistoricoAcaoComprovanteDespesa:",
      resp?.data,
    );
    return [];
  } catch (e) {
    console.error("Erro ao buscar histórico de comprovantes:", e);
    return [];
  }
};

// Salva a análise dos comprovantes de despesas feita pelo analista
// Endpoint: POST /api/financeiro/salvarComprovantesDespesasAnalise
export const salvarComprovantesDespesasAnalise = async (payload) => {
  const corpo = {
    idSindicato: Number(payload.idSindicato),
    solicitarCorrecao: !!payload.solicitarCorrecao,
    liberarTotalParcelas: !!payload.liberarTotalParcelas,
    obsAnalista: payload.obsAnalista,
    analisesDespesas: (payload.analisesDespesas || []).map((a) => ({
      id: Number(a?.id) || 0,
      obsAnalista: String(a?.obsAnalista ?? "").trim(),
      conforme: typeof a?.conforme === "boolean" ? a.conforme : null,
      idComprovanteDespesa: Number(a?.idComprovanteDespesa) || 0,
      dataSolicitacao: a?.dataSolicitacao
        ? new Date(a.dataSolicitacao).toISOString()
        : null,
    })),
  };

  try {
    const response = await api.post(
      "/api/financeiro/salvarComprovantesDespesasAnalise",
      corpo,
    );
    return response?.data ?? null;
  } catch (e) {
    console.error("Erro:", e);
    throw e;
  }
};

// Verifica se o arquivo pode ser aberto diretamente no navegador
const isRenderableInBrowser = (contentType = "") => {
  const t = contentType.toLowerCase();
  return (
    t.startsWith("application/pdf") ||
    t.startsWith("image/") ||
    t.startsWith("text/") ||
    t.startsWith("application/json")
  );
};

// Extrai o nome do arquivo a partir do header Content-Disposition
const getFilenameFromContentDisposition = (cd = "") => {
  if (!cd) return null;

  const fnStarMatch = cd.match(/filename\*\s*=\s*([^']*)''([^;]+)/i);
  if (fnStarMatch) {
    try {
      return decodeURIComponent(fnStarMatch[2]);
    } catch {
      return fnStarMatch[2];
    }
  }

  const fnMatch = cd.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  if (fnMatch)
    return (fnMatch[1] || fnMatch[2] || "").trim().replace(/^"(.*)"$/, "$1");

  return null;
};

// Infere a extensão do arquivo com base no content-type
const guessExtensionFromContentType = (ct = "") => {
  const map = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/vnd.ms-excel": "xls",
    "application/pdf": "pdf",
    "application/zip": "zip",
    "application/json": "json",
    "text/plain": "txt",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/webp": "webp",
  };
  const norm = ct.toLowerCase().split(";")[0].trim();
  return map[norm] || "";
};

// Garante que o nome do arquivo tenha extensão válida
const ensureFilenameWithExt = (baseName = "arquivo", contentType = "") => {
  if (/\.[a-z0-9]+$/i.test(baseName)) return baseName;
  const ext = guessExtensionFromContentType(contentType);
  return ext ? `${baseName}.${ext}` : baseName;
};

// Faz download ou abre no navegador uma despesa pelo identificador
// Endpoint: GET /api/financeiro/downloadDespesa/{identificador}
export const downloadDespesaByIdentificador = async (
  identificador,
  nomeArquivoComExtensao = "arquivo",
) => {
  try {
    const response = await api.get(
      `/api/financeiro/downloadDespesa/${identificador}`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  } catch (error) {
    console.error("❌ Erro ao obter documento:", error);
  }
};

// V2 (2026+): download do arquivo da despesa
// Endpoint: GET /api/despesas-extratos-avaliacoes/comprovantes-despesas/arquivo/{arquivoId}
export const downloadArquivoDespesaByArquivoId = async (
  arquivoId: number | string,
  nomeArquivoComExtensao = "comprovante.pdf",
) => {
  if (arquivoId == null || arquivoId === "") {
    throw new Error("arquivoId é obrigatório.");
  }

  try {
    const response = await api.get(
      `/api/despesas-extratos-avaliacoes/comprovantes-despesas/arquivo/${arquivoId}`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  } catch (error) {
    console.error("❌ Erro ao baixar arquivo de despesa (V2):", error);
    throw error;
  }
};

// V2 (2026+): download do arquivo de um evento do histórico de comprovantes
// Endpoint: GET /api/despesas-extratos-avaliacoes/extratos/{arquivoId}/arquivo-eventos
export const downloadDespesaExtratoArquivoEvento = async (
  arquivoId: number | string,
  nomeArquivoComExtensao = "comprovante_evento.pdf",
) => {
  if (arquivoId == null || arquivoId === "") {
    throw new Error("arquivoId é obrigatório.");
  }

  try {
    const response = await api.get(
      `/api/despesas-extratos-avaliacoes/extratos/${arquivoId}/arquivo-eventos`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  } catch (error) {
    console.error(
      "❌ Erro ao baixar arquivo de evento do comprovante (V2):",
      error,
    );
    throw error;
  }
};

// V2 (2026+): download do PDF do extrato da solicitação (BAIXAR EXTRATO)
// Endpoint: GET /api/despesas-extratos-avaliacoes/comprovante-despesas/extrato-solicitacao/{extratoSolicitacaoId}/arquivo
// Usa extratoSolicitacaoId do GET PAI (NÃO o comprovanteDespesasId).
export const downloadArquivoExtratoSolicitacao = async (
  extratoSolicitacaoId: number | string,
  nomeArquivoComExtensao = "extrato.pdf",
) => {
  if (extratoSolicitacaoId == null || extratoSolicitacaoId === "") {
    throw new Error("extratoSolicitacaoId é obrigatório.");
  }

  try {
    const response = await api.get(
      `/api/despesas-extratos-avaliacoes/comprovante-despesas/extrato-solicitacao/${extratoSolicitacaoId}/arquivo`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    return { url, filename, blob };
  } catch (error) {
    console.error(
      "❌ Erro ao baixar arquivo do extrato da solicitação (V2):",
      error,
    );
    throw error;
  }
};

// Solicita assinatura do comprovante com envio de arquivo em multipart/form-data
// Endpoint: POST /api/financeiro/solicitarAssinaturaComprovante
export const solicitarAssinaturaComprovante = async (payload) => {
  try {
    const formData = new FormData();

    formData.append("idSindicato", String(payload.idSindicato ?? ""));
    formData.append(
      "EmailSignatario",
      String(payload.EmailSignatario || "").trim(),
    );
    formData.append("Observacao", String(payload.Observacao || "").trim());

    if (payload.Arquivo && payload.Arquivo.length > 0) {
      payload.Arquivo.forEach((file) => {
        formData.append("Arquivo", file);
      });
    }

    await api.post("/api/financeiro/solicitarAssinaturaComprovante", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return true;
  } catch (e) {
    return false;
  }
};

// Faz upload do comprovante de assinatura em multipart/form-data
// Endpoint: POST /api/financeiro/uploadComprovanteAssinatura
export const uploadComprovanteAssinatura = async ({
  idSindicato,
  idSolicitacao,
  codigoFusion,
  arquivo,
}) => {
  if (!idSindicato) throw new Error("Id do sindicato é obrigatório.");
  if (!idSolicitacao) throw new Error("Id da solicitação é obrigatório.");
  if (!codigoFusion || !String(codigoFusion).trim())
    throw new Error("Código Fusion é obrigatório.");
  if (!arquivo) throw new Error("Selecione um PDF para enviar.");

  const formData = new FormData();
  formData.append("IdSindicato", String(idSindicato));
  formData.append("IdSolicitacao", String(idSolicitacao));
  formData.append("CodigoFusion", String(codigoFusion).trim());
  formData.append("Arquivo", arquivo);

  try {
    const resp = await api.post(
      "/api/financeiro/uploadComprovanteAssinatura",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return resp?.data ?? true;
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: unknown } };
    console.error(
      "[uploadComprovanteAssinatura] ERRO:",
      err?.response?.status,
      err?.response?.data || e,
    );
    throw e;
  }
};

// Faz download ou abre no navegador o comprovante de despesa da solicitação
// Endpoint: GET /api/financeiro/downloadComprovanteDespesaSolicitacao/{identificador}
export const downloadComprovanteDespesaSolicitacaoByIdentificador = async (
  identificador,
  nomeArquivoComExtensao = "arquivo",
) => {
  try {
    const response = await api.get(
      `/api/financeiro/downloadComprovanteDespesaSolicitacao/${identificador}`,
      { responseType: "blob" },
    );

    const contentType = response.headers?.["content-type"] || "";
    const cd = response.headers?.["content-disposition"] || "";

    const blob = new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);

    const filenameFromHeader = getFilenameFromContentDisposition(cd);
    const filename = ensureFilenameWithExt(
      filenameFromHeader || nomeArquivoComExtensao,
      contentType,
    );

    if (isRenderableInBrowser(contentType)) {
      const win = window.open("", "_blank");
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  } catch (error) {
    console.error("❌ Erro ao obter documento:", error);
  }
};

// Busca a view de pagamento por id da solicitação
// Endpoint: GET /api/financeiro/getViewPagamento/{idSolicitacao}
export const getViewPagamentoByIdSolicitacao = async (idSolicitacao) => {
  try {
    const resp = await api.get(
      `/api/financeiro/getViewPagamento/${idSolicitacao}`,
    );
    return resp?.data || {};
  } catch (e) {
    console.error("Erro ao buscar view de pagamento:", e);
    throw e;
  }
};

// Salva os dados do pagamento da solicitação
// Endpoint: POST /api/financeiro/salvarPagamento
export const salvarPagamento = async (payload) => {
  try {
    const corpo = {
      idSolicitacao: Number(payload.idSolicitacao),
      valorLiquidoParcela: Number(payload.valorLiquidoParcela),
      idCondicaoPagamento: Number(payload.idCondicaoPagamento),
      observacao: String(payload.observacao ?? "").trim(),
    };

    const resp = await api.post("/api/financeiro/salvarPagamento", corpo);

    return resp.data;
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: unknown } };
    console.error(
      "❌ [FinanceiroService] Erro ao salvar pagamento:",
      err?.response?.status,
      err?.response?.data || e,
    );
    throw e;
  }
};

// Gera o pagamento da solicitação
// Endpoint: POST /api/financeiro/gerarPagamento
export const gerarPagamento = async (payload) => {
  try {
    const corpo = {
      idSolicitacao: Number(payload.idSolicitacao),
    };

    const resp = await api.post("/api/financeiro/gerarPagamento", corpo);
    return resp?.data ?? true;
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: unknown } };
    console.error(
      "❌ [FinanceiroService] Erro ao gerar pagamento:",
      err?.response?.status,
      err?.response?.data || e,
    );
    throw e;
  }
};
